<template>
  <div
    id="abot-chat-dialog"
    class="dialog-wrapper"
    :class="{
      'mobile-mode': isMobile,
      'local-mode': isLocalhost,
      'remote-mode': !isLocalhost,
    }"
  >
    <div
      ref="dialogElement"
      class="dialog-style"
      :class="{
        'remote-style': !isLocalhost,
        // 公告已改为随消息流内联展示（见 CustomerContainer.vue 的 inline-announcement），
        // 顶部悬浮 pill 及其占位空间不再需要，class 保持注释以便回滚。
        // 'has-announcement': props.announcementFlag,
        'compact-dialog': isCompactDialog,
      }"
      :style="{
        '--bg-color': brandStyle.color,
        '--abot-rating-star-gap': ratingStarGap,
        ...dialogStyle.cssVariables,
        ...brandStyle.cssVariables,
      }"
    >
      <div class="dialog-top-overlay">
        <ChatDialogHeader
          :logo="props.logo"
          :channel-name="props.channelName"
          @close="handleHeaderClose"
        />
        <!--
          公告已改为随消息流内联展示（见 CustomerContainer.vue 的 inline-announcement）。
          此处保留组件导入与模态弹窗，仅注释掉顶部悬浮 pill 的挂载点。
        <AnnouncementCard
          v-if="props.announcementFlag"
          :announcement="props.announcement"
          @open="announcementOpen = true"
        />
        -->
      </div>

      <div class="announcement-and-content">
        <div class="content-and-navigation">
          <!-- 聊天框主体 -->
          <CustomerContainer
            class="customer-container"
            :color="props.boxColor"
            :announcement-flag="props.announcementFlag"
            :announcement="props.announcement"
            :has-navigation="
              cStore.role !== 'agent' &&
              cStore.chatStatus === ChatStatus.ROBOT &&
              cStore.navigation.length !== 0
            "
          />

          <!-- 导航栏：仅访客视角，机器人接待且有可用导航项时才渲染 -->
          <ChatNavigation
            v-if="
              cStore.role !== 'agent' &&
              cStore.chatStatus === ChatStatus.ROBOT &&
              cStore.navigation.length !== 0
            "
            :items="cStore.navigation"
            @item-click="handleNavClick"
          />
        </div>
      </div>

      <!-- 输入框包装器：动态底部内边距 -->
      <div class="input-wrapper">
        <CustomerInputCard :color="brandStyle.color" />
      </div>

      <Transition name="modal-fade">
        <div
          v-if="announcementOpen"
          class="announcement-mask"
          @click.self="closeAnnouncement"
          @keydown.esc="closeAnnouncement"
        >
          <div
            class="announcement-dialog-stack"
            role="dialog"
            aria-modal="true"
            aria-labelledby="abot-announcement-title"
            aria-describedby="abot-announcement-content"
            tabindex="-1"
            @click.self="closeAnnouncement"
            @keydown.esc="closeAnnouncement"
          >
            <div class="announcement-modal">
              <h2 id="abot-announcement-title" class="announcement-title">
                {{ $t('session.announcement.title') }}
              </h2>
              <div id="abot-announcement-content" class="announcement-content" tabindex="0">
                <AutoLinkText :text="fullAnnouncement" />
              </div>
            </div>
            <button
              type="button"
              class="announcement-close-button"
              :aria-label="$t('common.close')"
              @click="closeAnnouncement"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path
                  d="M2 2L10 10"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2 10L10 2"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </Transition>

      <MatchQuestionsModal
        :open="matchQuestionsOpen"
        :items="matchQuestionsItems"
        :message-id="matchQuestionsMessageId"
        @close="closeMatchQuestions"
      />
    </div>
  </div>
</template>

<script lang="ts">
/**
 * Purpose: Render the chat-window shell and coordinate its modal, navigation,
 * viewport, responsive runtime-state layout, and teardown behavior. Announcement content scrolls
 * within the rendered dialog bounds while its title and close control remain visible.
 * Fixed transfers call the routing endpoint; questions use text messages and backend keyword routing.
 * Props: dialogStyle contains validated desktop dimensions; channel settings provide identity,
 * brand, and announcement data for the floating top overlay.
 * Emits: close fires for the remote embedded mode; localhost keeps its window-close behavior.
 */
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
  type PropType,
} from 'vue'
import CustomerInputCard from './CustomerInputCard.vue'
import CustomerContainer from './CustomerContainer.vue'
import ChatNavigation from './ChatNavigation.vue'
import { useChatStore } from '../../../store/chat'
import { ChatStatus, NavType } from '../../../enum/chatEnum'
import { transferAgent } from '../../../api/simple-api'
import { message } from '../../../hooks/message'
import { useI18n } from '../../../i18n/simple-i18n'
import { useChatInput } from '../../../hooks/useChatInput'
import AnnouncementCard from './AnnouncementCard.vue'
import { useLang } from '../../../hooks/useLang'
import { playSound, playSoundMute } from '../../../hooks/useSound'
import AutoLinkText from './AutoLinkText.vue'
import {
  WIDGET_DIALOG_STYLE_CONSTRAINTS,
  type WidgetDialogStyle,
} from '../../../utils/widgetDialogStyle'
import { normalizeWidgetBrandStyle } from '../../../utils/widgetBrandStyle'
import ChatDialogHeader from './ChatDialogHeader.vue'
import MatchQuestionsModal from './MatchQuestionsModal.vue'
import { type MatchQuestionItem, type OpenMatchQuestionsFn } from './matchQuestionsTypes'

interface ChatDialogSettings {
  announcementFlag: boolean
  announcement: Partial<Record<'zh-CN' | 'zh-TW' | 'en-US', string>>
  boxColor: string
  logo?: string
  channelName?: string
}

export default defineComponent({
  name: 'ChatDialog',
  components: {
    ChatDialogHeader,
    CustomerInputCard,
    CustomerContainer,
    ChatNavigation,
    AnnouncementCard,
    AutoLinkText,
    MatchQuestionsModal,
  },
  props: {
    props: {
      type: Object as PropType<ChatDialogSettings>,
      required: true,
    },
    dialogStyle: {
      type: Object as PropType<WidgetDialogStyle>,
      required: true,
    },
  },
  emits: {
    close: () => true,
  },
  setup(props, { emit }) {
    const screenWidth = ref(window.innerWidth)
    const dialogElement = ref<HTMLElement | null>(null)
    const renderedDialogWidth = ref(props.dialogStyle.width)
    const renderedDialogHeight = ref(props.dialogStyle.height)
    const cStore = useChatStore()

    let updateTimer: ReturnType<typeof setTimeout> | null = null
    let dialogResizeObserver: ResizeObserver | null = null
    let isUpdating = false

    const { getUCLang } = useLang()
    const announcementOpen = ref(false)
    const { sendTextMessage } = useChatInput()
    const { t } = useI18n()
    const isTransferring = ref(false)
    let disposed = false

    // matchQuestions 弹窗：由子组件 MatchQuestionsCard 通过 provide 的 openMatchQuestions 触发。
    // ChatDialog 只持有 open/items/messageId，弹窗 UI、提交状态与 queClicks 打点在 MatchQuestionsModal 内部。
    const matchQuestionsOpen = ref(false)
    const matchQuestionsItems = ref<MatchQuestionItem[]>([])
    const matchQuestionsMessageId = ref('')
    const openMatchQuestions: OpenMatchQuestionsFn = (payload) => {
      matchQuestionsItems.value = payload.items
      matchQuestionsMessageId.value = payload.messageId
      matchQuestionsOpen.value = true
    }
    const closeMatchQuestions = () => {
      matchQuestionsOpen.value = false
    }
    provide('openMatchQuestions', openMatchQuestions)
    const brandStyle = computed(() => normalizeWidgetBrandStyle(props.props.boxColor))
    const fullAnnouncement = computed(() => props.props.announcement[getUCLang()] ?? '')
    const isCompactDialog = computed(
      () =>
        renderedDialogWidth.value < WIDGET_DIALOG_STYLE_CONSTRAINTS.width.default ||
        renderedDialogHeight.value < WIDGET_DIALOG_STYLE_CONSTRAINTS.height.default,
    )
    const ratingStarGap = computed(() =>
      renderedDialogWidth.value < WIDGET_DIALOG_STYLE_CONSTRAINTS.width.default ? '16px' : '20px',
    )

    const syncRenderedDialogSize = () => {
      const dialog = dialogElement.value
      if (!dialog) return

      const bounds = dialog.getBoundingClientRect()
      renderedDialogWidth.value = bounds.width
      renderedDialogHeight.value = bounds.height
    }

    const closeAnnouncement = () => {
      announcementOpen.value = false
    }

    const handleAnnouncementKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeAnnouncement()
    }

    const handleHeaderClose = () => {
      if (isLocalhost.value) {
        window.close()
        return
      }

      emit('close')
    }

    const openExternalUrl = (url: string) => {
      url = url.trim()
      const urlPattern = /^(https?:\/\/)/i
      if (urlPattern.test(url)) {
        window.open(url, '_blank')
      } else {
        window.open('https://' + url, '_blank')
      }
    }

    /** Fixed transfers use HTTP; questions send their localized name through the normal message path. */
    const handleNavClick = async (item: {
      label: string
      value: string | number
      type: number
    }) => {
      if (item.type === NavType.LINK && typeof item.value === 'string') {
        openExternalUrl(item.value)
      } else if (item.type === NavType.QUESTION) {
        sendTextMessage(item.label)
      } else if (item.type === NavType.TRANSFER_AGENT) {
        if (isTransferring.value) return
        isTransferring.value = true
        try {
          const response = await transferAgent(cStore.channelId)
          const result: unknown = await response.json()
          if (disposed) return
          if (
            typeof result === 'object' &&
            result !== null &&
            'code' in result &&
            result.code === '00000'
          )
            return
          const detail =
            typeof result === 'object' &&
            result !== null &&
            'msg' in result &&
            typeof result.msg === 'string'
              ? result.msg
              : ''
          message.error(detail || t('session.chat.transfer.failed'))
        } catch {
          if (!disposed) message.error(t('session.chat.transfer.failed'))
        } finally {
          isTransferring.value = false
        }
      }
    }

    const IMG_URL = import.meta.env.VITE_IMG_BASE_URL

    const isLocalhost = computed(() => {
      return window.location.host === 'localhost:5100' || IMG_URL.includes(window.location.host)
    })

    const isMobile = computed(() => {
      return screenWidth.value <= 768
    })

    // 更新底部内边距
    // 在 Vue 组件中，通过 DOM 元素向上查找
    const updateBottomPadding = async () => {
      if (isUpdating) return
      isUpdating = true

      await nextTick()

      setTimeout(() => {
        try {
          // 通过组件根元素获取 shadowRoot
          const dialogWrapper = document.getElementById('abot-chat-dialog')
          if (!dialogWrapper) {
            // console.warn('未找到对话框元素')
            return
          }

          const rootNode = dialogWrapper.getRootNode()
          const shadowRoot = rootNode instanceof ShadowRoot ? rootNode : null

          if (!shadowRoot) {
            console.warn('不在 Shadow DOM 中')
            return
          }

          let inputCard = shadowRoot.querySelector('.customer-input-card')
          if (!inputCard) {
            inputCard = shadowRoot.querySelector('.chat-input-card')
          }
          if (!inputCard) {
            console.warn('未找到输入框元素')
            return
          }

          // ... 后续逻辑
        } catch (error) {
          console.error('更新底部边距失败:', error)
        } finally {
          isUpdating = false
        }
      }, 50)
    }

    const debouncedUpdate = () => {
      if (updateTimer) clearTimeout(updateTimer)
      updateTimer = setTimeout(() => {
        updateBottomPadding()
      }, 150)
    }

    const forceUpdate = () => {
      if (updateTimer) clearTimeout(updateTimer)
      updateBottomPadding()
    }

    const handleResize = () => {
      screenWidth.value = window.innerWidth
      syncRenderedDialogSize()
      debouncedUpdate()
    }

    watch(announcementOpen, (isOpen) => {
      if (isOpen) {
        window.addEventListener('keydown', handleAnnouncementKeydown)
        return
      }

      window.removeEventListener('keydown', handleAnnouncementKeydown)
    })

    watch(
      () => cStore.chatStatus,
      () => {
        nextTick(() => forceUpdate())
      },
    )

    watch(
      () => cStore.chatInfo.length,
      () => {
        nextTick(() => forceUpdate())
      },
    )

    const handleSwitchSound = () => {
      playSound('/music/message.mp3', 0)
    }

    onMounted(() => {
      handleSwitchSound()
      playSoundMute('/music/message.mp3')

      syncRenderedDialogSize()
      if (typeof ResizeObserver !== 'undefined' && dialogElement.value) {
        dialogResizeObserver = new ResizeObserver(syncRenderedDialogSize)
        dialogResizeObserver.observe(dialogElement.value)
      }

      setTimeout(forceUpdate, 100)
      setTimeout(forceUpdate, 300)
      setTimeout(forceUpdate, 500)

      window.addEventListener('resize', handleResize)
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize)
      }
      document.addEventListener('focusin', debouncedUpdate)
      document.addEventListener('focusout', forceUpdate)

      console.log('Current host:', window.location.host)
      console.log('Is mobile?', isMobile.value)
    })

    onUnmounted(() => {
      disposed = true
      window.removeEventListener('resize', handleResize)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize)
      }
      document.removeEventListener('focusin', debouncedUpdate)
      document.removeEventListener('focusout', forceUpdate)
      window.removeEventListener('keydown', handleAnnouncementKeydown)
      dialogResizeObserver?.disconnect()
      if (updateTimer) clearTimeout(updateTimer)
    })

    return {
      cStore,
      isMobile,
      isLocalhost,
      brandStyle,
      closeAnnouncement,
      dialogElement,
      handleNavClick,
      handleHeaderClose,
      announcementOpen,
      fullAnnouncement,
      isCompactDialog,
      ratingStarGap,
      matchQuestionsOpen,
      matchQuestionsItems,
      matchQuestionsMessageId,
      closeMatchQuestions,
      ChatStatus,
    }
  },
})
</script>

<style lang="less" scoped>
.dialog-style {
  position: absolute;
  box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.1);
  margin: 0;
  background-color: #f9faf9;
  border-radius: var(--abot-dialog-radius);
  width: var(--abot-dialog-width);
  height: var(--abot-dialog-height);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  --abot-dialog-top-offset: calc(76px + env(safe-area-inset-top, 0px));

  &.has-announcement {
    --abot-dialog-top-offset: calc(120px + env(safe-area-inset-top, 0px));
  }

  &.remote-style {
    bottom: 0px;
    right: 0px;
  }
}

.dialog-top-overlay {
  position: absolute;
  top: calc(16px + env(safe-area-inset-top, 0px));
  right: 16px;
  left: 16px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
}

.announcement-and-content {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.content-and-navigation {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 0;
  flex: 1;
}

/* 输入框包装器 */
.input-wrapper {
  flex-shrink: 0;
  background-color: #f9faf9;
}

/* CustomerContainer 自动占据剩余空间并滚动 */
.customer-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-top: var(--abot-dialog-top-offset);
}

/* 让内部的聊天容器滚动 */
.customer-container :deep(.chat-container) {
  flex: 1;
  min-height: 0;
  overflow-y: auto !important;
}

/* ========== localhost:5100 模式 ========== */
.local-mode {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  box-sizing: border-box;

  &.mobile-mode {
    padding: 0;

    .dialog-style {
      width: 100vw !important;
      width: 100dvw !important;
      height: 100vh !important;
      height: 100dvh !important;
      min-height: 300px;
      margin: 0;
      border-radius: 0;
    }
  }

  .dialog-style {
    max-width: 100%;
    max-height: 100%;
  }
}

/* ========== 远程模式 ========== */
.remote-mode {
  position: fixed;
  z-index: 9999;
  box-sizing: border-box;
  width: 100vw;
  height: 100vh;
}

@media (min-width: 769px) {
  .remote-mode {
    position: absolute;
    margin: -20px;
  }
}

/* 移动端远程模式 */
.remote-mode.mobile-mode {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  margin: 0;
  padding: 0;

  .dialog-style {
    width: 100vw !important;
    width: 100dvw !important;
    height: 100vh !important;
    height: 100dvh !important;
    margin: 0;
    border-radius: 0;
  }
}

/* 公告弹窗 */
.announcement-mask {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding-top: max(24px, env(safe-area-inset-top, 0px));
  padding-bottom: max(24px, env(safe-area-inset-bottom, 0px));
  align-items: center;
  justify-content: center;
  background: rgba(32, 34, 33, 0.38);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.announcement-dialog-stack {
  display: flex;
  width: calc(100% - 60px);
  height: 100%;
  min-height: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.announcement-modal {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  max-height: calc(100% - 36px);
  min-height: 0;
  padding: 24px 36px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  border-radius: 12px;
  background: #fff;
}

.compact-dialog .announcement-mask {
  padding-top: max(16px, env(safe-area-inset-top, 0px));
  padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
}

.compact-dialog .announcement-modal {
  padding: 16px 24px;
}

.announcement-title {
  align-self: stretch;
  flex-shrink: 0;
  margin: 0;
  color: var(--text-primary, #202221);
  font-family: inherit;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  text-align: left;
}

.announcement-content {
  width: 100%;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  color: var(--text-primary, #202221);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  overflow-wrap: anywhere;
  scrollbar-color: var(--text-4, #b8bbb8) transparent;
  scrollbar-width: thin;
  text-align: left;
  -webkit-overflow-scrolling: touch;

  &:focus-visible {
    outline: 2px solid var(--abot-brand-color);
    outline-offset: 2px;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 100px;
    background: var(--text-4, #b8bbb8);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--text-3, #979c98);
  }
}

.announcement-close-button {
  box-sizing: border-box;
  display: flex;
  width: 24px;
  height: 24px;
  padding: 0;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 100px;
  background: rgba(0, 0, 0, 0.6);
  color: #9c9f9d;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
}

/* matchQuestions 弹窗样式已移至 MatchQuestionsModal.vue */

.modal-fade-enter-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-leave-active {
  transition: opacity 0.2s cubic-bezier(0.4, 0, 1, 1);
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .announcement-dialog-stack,
.modal-fade-leave-active .announcement-dialog-stack {
  transition: transform 0.2s ease;
}

.modal-fade-enter-from .announcement-dialog-stack,
.modal-fade-leave-to .announcement-dialog-stack {
  transform: scale(0.96);
}
</style>
